<script setup lang="ts">
// One filter bar for every agent list: search, tag chips, online-only and
// (optionally) group-by. State lives in the `AgentFilter` passed in, so the
// caller decides whether it is URL-synced (dashboard) or local (pickers).
import { useI18n } from "vue-i18n";

import type { AgentFilter } from "@/composables/useAgentFilter";

defineProps<{
  filter: AgentFilter;
  /** Show the group-by selector (lists that render groups). */
  showGroup?: boolean;
}>();

const { t } = useI18n();
</script>

<template>
  <div class="agent-filter" data-test="agent-filter">
    <div class="agent-filter__row">
      <input
        type="search"
        :value="filter.query"
        class="agent-filter__search"
        :placeholder="t('filter.search')"
        data-test="agent-filter-query"
        @input="filter.setQuery(($event.target as HTMLInputElement).value)"
      />
      <label class="agent-filter__online">
        <input
          type="checkbox"
          :checked="filter.onlineOnly"
          data-test="agent-filter-online"
          @change="filter.setOnlineOnly(($event.target as HTMLInputElement).checked)"
        />
        <span>{{ t("filter.online_only") }}</span>
      </label>
      <label v-if="showGroup && filter.tagKeys.length" class="agent-filter__group">
        <span>{{ t("filter.group_by") }}</span>
        <select
          :value="filter.groupBy"
          data-test="agent-filter-group"
          @change="filter.setGroupBy(($event.target as HTMLSelectElement).value)"
        >
          <option value="">{{ t("filter.group_none") }}</option>
          <option v-for="k in filter.tagKeys" :key="k" :value="k">{{ k }}</option>
        </select>
      </label>
      <span class="agent-filter__count" data-test="agent-filter-count">
        {{ t("filter.shown", { n: filter.filtered.length, total: filter.total }) }}
      </span>
      <button
        v-if="filter.active"
        type="button"
        class="agent-filter__clear"
        @click="filter.clear()"
      >
        {{ t("filter.clear") }}
      </button>
    </div>
    <div v-if="filter.allTags.length" class="agent-filter__tags">
      <button
        v-for="tag in filter.allTags"
        :key="tag"
        type="button"
        class="tag"
        :data-active="filter.tags.includes(tag) ? 'true' : undefined"
        :data-test="`agent-filter-tag-${tag}`"
        @click="filter.toggleTag(tag)"
      >
        {{ tag }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.agent-filter {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.agent-filter__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}
.agent-filter__search {
  flex: 1 1 14rem;
  min-width: 10rem;
}
.agent-filter__online,
.agent-filter__group {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  white-space: nowrap;
}
.agent-filter__count {
  font-size: 0.8rem;
  color: var(--color-fg-dim);
  white-space: nowrap;
}
.agent-filter__clear {
  font-size: 0.8rem;
}
.agent-filter__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
</style>
